import type { ReactNode } from 'react';
import type { AcademicDashboardResponse } from '../../../api/types';
import { Chip, KpiCard, Track } from '../../../components/ui';
import RingProgress from '../../../components/charts/RingProgress';
import { deltaChip, deltaChipWhole, pct, signed } from './format';

const chipOf = (c: { tone: 'up' | 'down' | 'neutral'; text: string } | null): ReactNode => (c ? <Chip tone={c.tone}>{c.text}</Chip> : undefined);

// A value beside a ring or under a sparkline needs a custom body, so these two cards build their own
// value row; the others use the shared KpiCard value (`size="lg"` = the design's 38px `.kv`).
const Value = ({ v, unit }: { v: string; unit?: string }) => (
  <div className="mt-2 whitespace-nowrap font-mono text-[38px] font-semibold leading-none tracking-[-0.04em]">
    {v}{unit && <span className="text-[18px]">{unit}</span>}
  </div>
);

export default function KpiGrid({ d }: { d: AcademicDashboardResponse }) {
  const h = d.headline;
  const avg = deltaChip(h.delta_4w);
  const spark = d.weekly_trend.map((w) => w.avg_score);
  const active = h.students ? (h.students_with_data / h.students) * 100 : 0;
  const enrolled = d.cycles.students_enrolled;
  const improved = d.cycles.students_improved;
  const hasSplit = enrolled != null && improved != null;
  return (
    <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
      <KpiCard label="Average score" chip={<Chip tone={avg.tone}>{avg.text}</Chip>} sub="vs prior 4 weeks · all grades">
        <Value v={h.avg_score == null ? '—' : h.avg_score.toFixed(1)} unit={h.avg_score == null ? undefined : '%'} />
        <MiniArea values={spark} />
      </KpiCard>
      <KpiCard label="Pass rate" chip={chipOf(deltaChipWhole(h.pass_rate_delta_4w, ' pts'))}
        sub={h.pass_rate == null ? `No students at or above ${d.pass_score_pct}% yet` : `${Math.round((h.students_with_data * h.pass_rate) / 100)} of ${h.students_with_data} students at or above ${d.pass_score_pct}%`}>
        <div className="flex items-center justify-between gap-3">
          <Value v={h.pass_rate == null ? '—' : String(Math.round(h.pass_rate))} unit={h.pass_rate == null ? undefined : '%'} />
          <RingProgress value={h.pass_rate ?? 0} label="Pass rate" />
        </div>
      </KpiCard>
      <KpiCard label="Active students" size="lg" chip={chipOf(deltaChipWhole(h.active_delta_4w, '%'))}
        value={String(h.students_with_data)} unit={<span className="text-muted"> / {h.students}</span>}
        track={<Track value={active} label="Students with exam data" />}
        sub={`${h.students - h.students_with_data} students with no exam data yet`} />
      <KpiCard label="Remediation gain" size="lg" chip={<Chip tone="accent">{d.cycles.total} {d.cycles.total === 1 ? 'cycle' : 'cycles'}</Chip>}
        value={signed(d.cycles.mean_gain)} unit={d.cycles.mean_gain == null ? undefined : ' pts'}
        tone={d.cycles.mean_gain != null && d.cycles.mean_gain > 0 ? 'success' : 'default'}
        track={hasSplit
          ? <SplitBar improved={improved} enrolled={enrolled} />
          : <Track value={d.cycles.crossed_pass_share ?? 0} label="Cycles that crossed the pass mark" />}
        sub={hasSplit ? `${enrolled} students enrolled · ${improved} improved` : `${d.cycles.active} active · ${pct(d.cycles.crossed_pass_share, 0)} crossed the pass mark`} />
    </div>
  );
}

/** Design split bar: improved : not improved, two 8px pills 4px apart. */
function SplitBar({ improved, enrolled }: { improved: number; enrolled: number }) {
  const rest = Math.max(0, enrolled - improved);
  if (enrolled <= 0) return <Track value={0} label="Students improved" />;
  return (
    <div className="flex gap-1" role="img" aria-label={`${improved} of ${enrolled} enrolled students improved`}>
      <span className="h-2 rounded-[4px] bg-slate" style={{ flex: improved }} />
      <span className="h-2 rounded-[4px] bg-track" style={{ flex: rest }} />
    </div>
  );
}

function MiniArea({ values }: { values: (number | null)[] }) {
  const xs = values.map((v, i) => ({ v, i })).filter((p): p is { v: number; i: number } => p.v != null);
  if (xs.length < 2) return <div className="h-12" />;
  const lo = Math.min(...xs.map((p) => p.v)), hi = Math.max(...xs.map((p) => p.v));
  const X = (i: number) => (i / Math.max(1, values.length - 1)) * 220;
  const Y = (v: number) => 44 - ((v - lo) / Math.max(1, hi - lo)) * 38;
  const line = xs.map((p, k) => `${k ? 'L' : 'M'}${X(p.i).toFixed(1)},${Y(p.v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 220 48" preserveAspectRatio="none" className="mt-1.5 h-12 w-full" aria-hidden="true">
      <path d={`${line} L${X(xs[xs.length - 1].i)},48 L${X(xs[0].i)},48 Z`} className="fill-slate" fillOpacity={0.14} />
      <path d={line} fill="none" className="stroke-slate-ink" strokeWidth={2.2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
